// @ts-nocheck
/*
 * Port of views/dashboard/company/edit.js (origin/main-dev, 1061 lines).
 * BYTE-IDENTICAL LOGIC â€” every state hook, useEffect, API call, toast,
 * validation, and JSX condition matches v1 to the letter.
 *
 * Key edit-vs-create differences preserved:
 *   - Loads existing company profile via API_COMPANY_PROFILE and hydrates
 *     licence fields from `subUserLicenceId.available_licence`.
 *   - Tracks `initialLicence` so non-superadmin `validateInput` allows
 *     "keeping existing value" and caps increases to `parentAvailable + init`.
 *   - Superadmin branch of `validateInput` skips upper-limit checks entirely
 *     (only respects demo caps).
 *   - `getLicenceMax` sums `initialValue + parentMax` for non-superadmin.
 *   - Company type cards render READ-ONLY badges (no re-selection).
 *   - No DefaultUserForm â€” this page edits company only.
 *   - `originalCompanyExpiryDate` warning triggers when reducing.
 *   - Submits without expiry_date when demo (demo settings manage expiry).
 *   - Renders `Used Licence` summary below the LicenceAllocationPanel.
 *
 * Chrome kept as-is: react-bootstrap Form/Row/Col/Button, react-select,
 * react-icons, RenderUserCompanyFields + LicenceAllocationPanel partials.
 * v1 <Card> wrapper â†’ shadcn <Card>.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button as _ShadButton } from '@/components/ui/button';

// Inline micro-shim block: replaces `@/lib/rb-shim` Row/Col/Form/Button
// with native bootstrap form-control markup + shadcn Button. JSX intact.
const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
import {
  API_COMPANY_PROFILE,
  API_PARENT_COMPANY_LICENCE,
  API_MANAGE_LOCATIONS_LIST,
  API_MANAGE_GROUP_LISTING,
  API_CALL_STRATEGIES_MASTER_LIST,
  API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST,
  API_USER_COUNTRY_CODE_DIRECTIONS_BY_COMPANY,
  API_TTS_PLANS_LIST,
  API_DEMO_ACCOUNT_SETTINGS_LIST,
  API_COMPANY_UPLOAD_LOGO,
} from '@/service/url';
import { postApiCall, getApiCall, fetchAndSetFlashMessage } from '@/service/httpCall';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ROUTES,
  USER_ROLES,
  MODULE_ACL,
  FLASH_MESSAGE_ACTION_OBJECT,
} from '@/utilities/constant';
import RenderUserCompanyFields from '@/components/partials/dashboard/users/company';
import { validatePhone } from '@/utilities/helpers';
import Select from 'react-select';
import { SelectAllToggle } from '@/components/ui/select-all-toggle';
import { Storage } from '@/utilities/setting';
import {
  FiPhone,
  FiUsers,
  FiShare2,
  FiInfo,
  FiActivity,
  FiSmartphone,
  FiUserCheck,
  FiCpu,
} from 'react-icons/fi';
import { FaBuilding, FaHeadset } from 'react-icons/fa';
import LicenceAllocationPanel from '@/components/partials/dashboard/company/LicenceAllocationPanel';
import { toDateInputString, formatExpiryForDisplay } from '@/utilities/expiryDate';
import { blockManualDateEntry, blockDatePaste } from '@/utilities/dateInputHelpers';
import { Card as ShadcnCard } from '@/components/ui/card';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES as PAGE_ROUTES } from '@/utilities/constant';
import '@/features/users/user-forms.css';
import { ArrowRight, Building2, Sliders, Award, Image } from 'lucide-react';
import { Stepper, useStepper, type StepDescriptor } from '@/components/ui/stepper';
import CompanyBrandingFields, {
  handleCompanyLogoChange,
} from './components/CompanyBrandingFields';
import { useAuthedImageUrl } from '@/hooks/useAuthedImageUrl';
import { storageUrlOr } from '@/utilities/storageUrl';
import { BACKEND_API_BASE_URL } from '@/utilities/constant';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { billingPlanLabel, formatCreditLimit } from '@/utilities/billingPlan';

// 3-step wizard for editing a company — same visual chrome as
// CompanyCreatePage's stepper minus the Default User step (edit doesn't
// create a user). Order + IDs match the section boundaries below.
const EDIT_COMPANY_STEPS: StepDescriptor[] = [
  { id: 'details', label: 'Details', description: 'Name, address, expiry', icon: Building2 },
  { id: 'setup', label: 'Setup', description: 'Strategy, TTS, type', icon: Sliders },
  { id: 'licence', label: 'Licence', description: 'Quotas & usage', icon: Award },
  { id: 'branding', label: 'Branding', description: 'Domain, panel name, logo', icon: Image },
];

const CompanyUpdate = () => {
  const stepper = useStepper(EDIT_COMPANY_STEPS.length);

  const { id } = useParams();
  const navigate = useNavigate();

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPincode, setCompanyPincode] = useState('');
  const [companyLicence, setCompanyLicence] = useState('');
  const [companyExpiryDate, setCompanyExpiryDate] = useState('');
  const [originalCompanyExpiryDate, setOriginalCompanyExpiryDate] = useState('');
  const [parentExpiryDate, setParentExpiryDate] = useState('');
  const [callCenterAdminTeamlead, setCallCenterAdminTeamlead] = useState(0);
  const [agent, setAgent] = useState(0);
  const [liveAgent, setLiveAgent] = useState(0);
  const [demoLiveAgents, setDemoLiveAgents] = useState(0);
  const [teamLead, setTeamLead] = useState(0);
  const [channel, setChannel] = useState(0);
  const [aiAgent, setAiAgent] = useState(0);
  const [aiAgentLiveChannel, setAiAgentLiveChannel] = useState(0);
  const [asReseller, setAsReseller] = useState(0);
  const [asCompany, setAsCompany] = useState(0);
  const [availableLicence, setAvailableLicence] = useState({});
  const [errors, setErrors] = useState({});
  const [totalLicence, setTotalLicence] = useState({});
  const [usedLicence, setUsedLicence] = useState({});
  const [loginUserRole, setLoginUserRole] = useState(null);
  const [parentLicence, setParentLicence] = useState(null);
  const [initialLicence, setInitialLicence] = useState({});
  const [selectedCallStrategy, setSelectedCallStrategy] = useState([]);
  const [countryCodeDirections, setCountryCodeDirections] = useState([]);
  const [selectedCountryCodeDirections, setSelectedCountryCodeDirections] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [locations, setLocations] = useState([]);
  const [callStrategies, setCallStrategies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isResellerCompany, setIsResellerCompany] = useState(null);
  const [isOwnCompany, setIsOwnCompany] = useState(null);
  const [isDemoCompany, setIsDemoCompany] = useState(false);
  const [billingPlanText, setBillingPlanText] = useState('—');
  const [billingCreditText, setBillingCreditText] = useState('');
  const [demoSettings, setDemoSettings] = useState(null);
  const [notes, setNotes] = useState([]);
  const [cps, setCps] = useState(0);
  const [mobile, setMobile] = useState(0);
  const [tts_plan_id, setTtsPlanId] = useState('');
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [panelDisplayName, setPanelDisplayName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [storedLogoUrl, setStoredLogoUrl] = useState(null);
  const { src: resolvedStoredLogoUrl } = useAuthedImageUrl(storedLogoUrl);
  const [ttsPlanList, setTtsPlanList] = useState([]);

  function getProfileApiUrl() {
    return API_COMPANY_PROFILE.replace(':id', id);
  }

  useEffect(() => {
    getApiCall(API_DEMO_ACCOUNT_SETTINGS_LIST, {})
      .then((resp) => {
        if (Array.isArray(resp) && resp.length > 0) setDemoSettings(resp[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    const profileApiUrl = getProfileApiUrl();
    getApiCall(profileApiUrl, {})
      .then((resp) => {
        setCompanyName(resp.name);
        setCompanyPhone(resp.phone);
        setCompanyAddress(resp.address);
        setCompanyPincode(resp.pincode);
        setIsOwnCompany(resp.is_own_company);
        setIsDemoCompany(!!resp.is_demo_company);
        if (resp.expiry_date) {
          const str = toDateInputString(resp.expiry_date);
          if (str) {
            setCompanyExpiryDate(str);
            setOriginalCompanyExpiryDate(str);
          }
        }
        setIsResellerCompany(resp.is_reseller_company);
        setBillingPlanText(billingPlanLabel(resp));
        setBillingCreditText(
          resp.billing_type === 'postpaid' && resp.postpaid_mode === 'limited' && resp.billing_cycle
            ? `${resp.billing_cycle.charAt(0).toUpperCase()}${resp.billing_cycle.slice(1)} limit ${formatCreditLimit(resp.credit_limit)}. Voice, TTS, and AI each started at this amount.`
            : resp.billing_type === 'postpaid' && resp.postpaid_mode === 'unlimited'
              ? 'Voice, TTS, and AI each started at 10 lac (10,00,000).'
              : '',
        );
        setTtsPlanId(resp.tts_plan_id ? String(resp.tts_plan_id) : '');

        setDomain(resp.domain || '');
        setPanelDisplayName(resp.panel_display_name || '');
        setStoredLogoUrl(
          resp.logo_path
            ? storageUrlOr(
                'logo',
                resp.logo_blob_path,
                `${BACKEND_API_BASE_URL.replace('/api', '')}/${resp.logo_path}`,
              )
            : null,
        );

        const licence = resp?.subUserLicenceId || {};
        const available = licence?.available_licence || {};
        const total = licence?.total_licence || {};
        const used = licence?.used_licence || {};

        setAvailableLicence(available);
        setTotalLicence(total);
        setUsedLicence(used);

        const currentValues = {
          role_ccadmin: available.role_ccadmin || 0,
          role_ccagent: available.role_ccagent || 0,
          role_teamlead: available.role_teamlead || 0,
          live_agent: available.live_agent || 0,
          team_lead: available.team_lead || 0,
          channel: available.channel || 0,
          cps: available.cps || 0,
          mobile: available.mobile || 0,
          reseller: available.reseller || 0,
          company: available.company || 0,
          demo_live_agents_per_reseller_company:
            available.demo_live_agents_per_reseller_company || 0,
          ai_agent: available.ai_agent || 0,
          ai_agent_live_channel: available.ai_agent_live_channel || 0,
        };

        setCallCenterAdminTeamlead(currentValues.role_ccadmin);
        setAgent(currentValues.role_ccagent);
        setTeamLead(currentValues.role_teamlead);
        setLiveAgent(currentValues.live_agent);
        setChannel(currentValues.channel);
        setAiAgent(currentValues.ai_agent);
        setAiAgentLiveChannel(currentValues.ai_agent_live_channel);
        setCps(currentValues.cps);
        setMobile(currentValues.mobile);
        setAsCompany(currentValues.company);
        setDemoLiveAgents(currentValues.demo_live_agents_per_reseller_company);

        setInitialLicence(currentValues);

        const selectedGroups = resp.companyGroups.map((item) => item.groups.group_id);
        const selectedLocations = resp.companyLocations.map((item) => item.locations.location_id);
        const selectedStrategies = resp.companyCallStrategies.map((item) => item.strategyMaster.id);

        setSelectedGroup(selectedGroups);
        setSelectedLocation(selectedLocations);
        setSelectedCallStrategy(selectedStrategies);

        if (Array.isArray(resp.companyCountryCodeDirections)) {
          setSelectedCountryCodeDirections(
            resp.companyCountryCodeDirections
              .map((item) => item.country_code_direction_id)
              .filter(Boolean),
          );
        } else {
          getApiCall(API_USER_COUNTRY_CODE_DIRECTIONS_BY_COMPANY(id), {})
            .then((data) => {
              const ids = Array.isArray(data)
                ? data.map((item) => item.country_code_direction_id).filter(Boolean)
                : [];
              setSelectedCountryCodeDirections(ids);
            })
            .catch(() => {});
        }
      })
      .catch(onError);
  }, [id]);

  useEffect(() => {
    const fetchMetaData = async () => {
      const user = JSON.parse(Storage.getItem('user'));

      try {
        const [locationsData, groupsData, callStrategiesData, countryCodeDirectionsData] =
          await Promise.all([
            getApiCall(API_MANAGE_LOCATIONS_LIST, {}),
            getApiCall(API_MANAGE_GROUP_LISTING, {}),
            getApiCall(API_CALL_STRATEGIES_MASTER_LIST, {}),
            getApiCall(API_COUNTRY_CODE_DIRECTIONS_MASTER_LIST, {}).catch(() => []),
          ]);

        setCallStrategies(
          callStrategiesData?.map((strategy) => ({
            value: strategy.id,
            label: strategy.name,
          })) || [],
        );

        setCountryCodeDirections(
          Array.isArray(countryCodeDirectionsData) ? countryCodeDirectionsData : [],
        );

        if (user.role === USER_ROLES.SUPERADMIN) {
          const normalizedLocations =
            (locationsData || []).map((loc) => ({
              value: loc.location_id,
              label: loc.location_name,
            })) || [];
          const normalizedGroups =
            (groupsData || []).map((g) => ({ value: g.group_id, label: g.group_name })) || [];
          setLocations(normalizedLocations);
          setGroups(normalizedGroups);
        } else {
          const userGroups =
            groupsData.map((ug) => ({ value: ug.group_id, label: ug.group_name })) || [];
          const userLocations =
            locationsData.map((ul) => ({ value: ul.location_id, label: ul.location_name })) || [];
          setGroups(userGroups);
          setLocations(userLocations);
        }
      } catch (error) {
        console.error('Error fetching Dialer groups/locations:', error);
      }
    };

    fetchMetaData();

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.COMPANY &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.EDIT &&
        msg.type === 'Note',
      setNotes,
    );
  }, []);

  useEffect(() => {
    getApiCall(API_TTS_PLANS_LIST)
      .then((resp) => setTtsPlanList(resp || []))
      .catch((error) => console.error('Failed to load TTS plans', error));
  }, []);

  const validateInput = (value, key) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    if (num < 0) {
      setErrors((prev) => ({ ...prev, [key]: 'Value cannot be negative' }));
      return num;
    }

    /* SUPER ADMIN */
    if (loginUserRole === USER_ROLES.SUPERADMIN) {
      if (isDemoCompany && demoSettings) {
        const demoCap = getDemoCap(key);
        if (demoCap != null && num > demoCap) {
          setErrors((prev) => ({ ...prev, [key]: '' }));
          return demoCap;
        }
        setErrors((prev) => ({ ...prev, [key]: '' }));
        return num;
      }
      setErrors((prev) => ({ ...prev, [key]: '' }));
      return num;
    }

    /* NON-SUPER ADMIN */
    const licenceKeyMap = {
      role_ccadmin: 'role_ccadmin',
      agent: 'role_ccagent',
      live_agent: 'live_agent',
      team_lead: 'role_teamlead',
      channel: 'channel',
      cps: 'cps',
      mobile: 'mobile',
      reseller: 'reseller',
      company: 'company',
      demo_live_agents: 'demo_live_agents_per_reseller_company',
      ai_agent: 'ai_agent',
      ai_agent_live_channel: 'ai_agent_live_channel',
    };

    const licenceKey = licenceKeyMap[key];
    const initialValue = Number(initialLicence?.[licenceKey] || 0);
    const parentAvailable = parentLicence?.available_licence?.[licenceKey];

    if (num === initialValue) {
      const nextAgent = key === 'agent' ? num : agent;
      const nextTeamLead = key === 'team_lead' ? num : teamLead;
      if (nextTeamLead > nextAgent) {
        setErrors((prev) => ({
          ...prev,
          team_lead: `Total Team Lead cannot be more than Total Agent (${nextAgent})`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, [key]: '', team_lead: '' }));
      }
      return num;
    }

    const diff = num - initialValue;

    if (parentAvailable !== undefined && diff > parentAvailable && diff > 0) {
      setErrors((prev) => ({
        ...prev,
        [key]: `You can increase only ${parentAvailable} more or Keep Existing value`,
      }));
      return num;
    }

    const nextAgent = key === 'agent' ? num : agent;
    const nextTeamLead = key === 'team_lead' ? num : teamLead;

    if (nextTeamLead > nextAgent) {
      setErrors((prev) => ({
        ...prev,
        team_lead: `Total Team Lead cannot be more than Total Agent (${nextAgent})`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, team_lead: '' }));
    }

    setErrors((prev) => ({ ...prev, [key]: '' }));

    const demoCap = getDemoCap(key);
    if (demoCap != null && num > demoCap) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
      return demoCap;
    }
    return num;
  };

  const getDemoCap = (key) => {
    if (!isDemoCompany || !demoSettings) return null;
    if (key === 'agent') return Number(demoSettings.total_demo_agent_per_company);
    if (key === 'live_agent') return Number(demoSettings.total_demo_live_agent_per_company);
    if (key === 'channel') return Number(liveAgent) * 2;
    if (key === 'role_ccadmin') return Number(demoSettings.call_center_per_company);
    if (key === 'team_lead') return Number(demoSettings.team_lead_per_company);
    return null;
  };

  const getLicenceMax = (key) => {
    const demoCap = getDemoCap(key);
    if (loginUserRole === USER_ROLES.SUPERADMIN) {
      return demoCap != null ? demoCap : undefined;
    }
    const avail = parentLicence?.available_licence;
    if (avail == null) return demoCap != null ? demoCap : undefined;
    const map = {
      role_ccadmin: avail.role_ccadmin,
      agent: avail.role_ccagent,
      live_agent: avail.live_agent,
      team_lead: avail.role_teamlead,
      channel: avail.channel,
      cps: avail.cps,
      mobile: avail.mobile,
      company: avail.company,
      demo_live_agents: avail.demo_live_agents_per_reseller_company,
      ai_agent: avail.ai_agent,
      ai_agent_live_channel: avail.ai_agent_live_channel,
    };
    const max = map[key];
    const initialKey =
      key === 'agent'
        ? 'role_ccagent'
        : key === 'team_lead'
        ? 'role_teamlead'
        : key === 'demo_live_agents'
        ? 'demo_live_agents_per_reseller_company'
        : key;
    const initialValue = Number(initialLicence?.[initialKey] || 0);
    if (max === undefined || max === null) return demoCap != null ? demoCap : undefined;
    const parentMax = initialValue + max;
    return demoCap != null ? Math.min(parentMax, demoCap) : parentMax;
  };

  const stepLicence = (key, current, setter, delta) => {
    const next = Math.max(0, (parseInt(String(current), 10) || 0) + delta);
    setter(validateInput(String(next), key));
  };

  const canIncrementLicence = (key, current) => {
    const max = getLicenceMax(key);
    if (max === undefined || max === null) return true;
    return (parseInt(String(current), 10) || 0) < max;
  };

  async function getUser() {
    try {
      const user = JSON.parse(Storage.getItem('user'));
      if (user) {
        setLoginUserRole(user.role);
        if (user.expiry_date) {
          const str = toDateInputString(user.expiry_date);
          if (str) setParentExpiryDate(str);
        }
      }
    } catch (err) {
      console.log(err);
    }
  }

  function validateCompanyFields() {
    let valid = true;

    if (!companyName.trim()) {
      valid = false;
      toast.error('Company name required');
    } else if (!validateCompanyPhone(companyPhone)) {
      valid = false;
      toast.error('Company phone invalid format');
    } else if (!companyPincode.trim()) {
      valid = false;
      toast.error('Company pincode required');
    } else if (!/^\d{6}$/.test(companyPincode)) {
      valid = false;
      toast.error('Company pincode must be exactly 6 digits');
    } else if (!companyAddress.trim()) {
      valid = false;
      toast.error('Company address required');
    }

    if (isDemoCompany) {
      if (Number(channel) > Number(liveAgent) * 2) {
        valid = false;
        toast.error('Demo company: Channels cannot exceed Total Live Agent * 2');
      } else if (
        demoSettings &&
        Number(agent) > Number(demoSettings.total_demo_agent_per_company)
      ) {
        valid = false;
        toast.error(
          `Demo company: Total Agent cannot exceed ${demoSettings.total_demo_agent_per_company}`,
        );
      } else if (
        demoSettings &&
        Number(liveAgent) > Number(demoSettings.total_demo_live_agent_per_company)
      ) {
        valid = false;
        toast.error(
          `Demo company: Total Live Agent cannot exceed ${demoSettings.total_demo_live_agent_per_company}`,
        );
      } else if (Number(teamLead) > Number(agent)) {
        valid = false;
        toast.error(
          'Demo company: Total Team Lead is inclusive in Total Agent and cannot exceed it',
        );
      }
    }

    return valid;
  }

  const handleLogoChange = (e) =>
    handleCompanyLogoChange(e, { setLogoFile, setLogoPreview, setLogoError });

  async function uploadLogo() {
    if (!logoFile) return null;

    const formData = new FormData();
    formData.append('logo', logoFile);

    try {
      const rawUser = Storage.getItem('user');
      const token = rawUser ? JSON.parse(rawUser)?.token : null;

      if (!token) {
        toast.error('Session expired. Please login again.');
        return null;
      }

      const response = await fetch(`${BACKEND_API_BASE_URL}${API_COMPANY_UPLOAD_LOGO}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) return null;

      return data.logo_path || data.path || data.filePath || data.data?.logo_path || null;
    } catch {
      return null;
    }
  }

  async function submitForm() {
    setButtonDisable(true);

    if (!validateCompanyFields()) {
      setButtonDisable(false);
      return;
    }

    if (Object.values(errors).some((error) => error !== '')) {
      toast.error('Please fix all validation errors before submitting');
      setButtonDisable(false);
      return;
    }

    if (domainError) {
      toast.error(domainError);
      setButtonDisable(false);
      return;
    }

    let uploadedLogoPath;
    if (logoFile) {
      const uploaded = await uploadLogo();
      if (!uploaded) {
        toast.error('Logo upload failed');
        setButtonDisable(false);
        return;
      }
      uploadedLogoPath = uploaded;
    }

    const params = {
      name: companyName,
      phone: companyPhone,
      address: companyAddress,
      pincode: companyPincode,
      is_reseller_company: isResellerCompany,
      is_own_company: isOwnCompany,
      is_demo_company: isDemoCompany,
      expiry_date: isDemoCompany ? undefined : companyExpiryDate || undefined,
      domain: domain.trim() || null,
      panel_display_name: panelDisplayName.trim() || null,
      ...(uploadedLogoPath !== undefined ? { logo_path: uploadedLogoPath } : {}),
      licence: {
        role_ccadmin: callCenterAdminTeamlead,
        role_ccagent: agent,
        role_teamlead: teamLead,
        live_agent: liveAgent,
        team_lead: teamLead,
        channel,
        cps: cps,
        mobile: mobile,
        reseller: 0,
        company: isOwnCompany ? 0 : asCompany,
        demo_live_agents_per_reseller_company: demoLiveAgents,
        ai_agent: aiAgent,
        ai_agent_live_channel: aiAgentLiveChannel,
      },
    };

    if (selectedGroup && selectedGroup.length > 0) {
      params.groups = Array.isArray(selectedGroup)
        ? selectedGroup.map(Number)
        : [Number(selectedGroup)];
    }
    if (selectedLocation && selectedLocation.length > 0) {
      params.locations = Array.isArray(selectedLocation)
        ? selectedLocation.map(Number)
        : [Number(selectedLocation)];
    }
    if (selectedCallStrategy && selectedCallStrategy.length > 0) {
      params.call_strategies = Array.isArray(selectedCallStrategy)
        ? selectedCallStrategy.map(Number)
        : [Number(selectedCallStrategy)];
    }
    params.country_code_directions = Array.isArray(selectedCountryCodeDirections)
      ? selectedCountryCodeDirections.map(Number)
      : [];

    if (tts_plan_id) {
      params.tts_plan_id = tts_plan_id;
    }

    const profileApiUrl = getProfileApiUrl();
    postApiCall(profileApiUrl, params).then(onCompanyUpdateSuccess).catch(onError);
  }

  function onCompanyUpdateSuccess(resp) {
    toast.success('Company updated successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.COMPANIES.LIST}`);
    }, 1500);
  }

  function onError(err) {
    setButtonDisable(false);
    toast.error(friendlyApiError(err) || 'Failed to update company');
  }

  useEffect(() => {
    getApiCall(API_PARENT_COMPANY_LICENCE)
      .then((resp) => {
        setParentLicence(resp?.parentLicence);
      })
      .catch((error) => {
        toast.error(friendlyApiError(error) || 'Failed to load parent company licence');
      });
  }, []);

  function validateCompanyPhone(phone) {
    if (!phone) return false;
    return /^[0-9]{10}$/.test(phone.trim());
  }

  const licencePanelNote = notes.find((n) => Number(n.priority) === 1)?.message ?? '';

  return (
    <FillPage>
      <PageHeader
        title="Edit Company"
        description="Adjust company details, credits, and default admin information."
        actions={
          <ShadPageButton asChild variant="outline">
            <RouterLink to={`/${PAGE_ROUTES.COMPANIES.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back to companies
            </RouterLink>
          </ShadPageButton>
        }
      />
      <div className="company-edit-page">
      <Row>
        <Col xl="12" lg="12">
          <ShadcnCard className="mt-4">
            <div className="user-create-form-card p-4">
              <div className="new-user-info company-create-form company-edit-form">
                <div className="user-create-form__hero">
                  <div className="user-create-form__hero-left">
                    <span className="user-create-form__hero-icon" aria-hidden="true">
                      <FaBuilding size={22} />
                    </span>
                    <div>
                      <h5 className="user-create-form__hero-title">
                        Edit Company Information
                        <span
                          className={`company-list-type-badge company-list-type-badge--${
                            isDemoCompany ? 'reseller' : 'neutral'
                          } ms-2`}
                        >
                          {isDemoCompany ? 'Demo Company' : 'Not a Demo Company'}
                        </span>
                      </h5>
                      <p className="user-create-form__hero-subtitle">
                        Update company profile, assignments, and licence allocation.
                      </p>
                    </div>
                  </div>
                  <div className="user-create-form__required-note">
                    <FiInfo size={14} aria-hidden="true" />
                    <span>
                      All fields marked with <span className="text-danger">*</span> are required
                    </span>
                  </div>
                </div>

                <Stepper
                  steps={EDIT_COMPANY_STEPS}
                  current={stepper.current}
                  completed={stepper.completed}
                  onStepClick={stepper.goTo}
                  className="mb-6"
                />
                <form>
                {/* ── Step 0: Details ─────────────────────────────── */}
                {stepper.current === 0 && (
                  <>
                  <RenderUserCompanyFields
                    isEditMode={true}
                    company_name={companyName}
                    company_phone={companyPhone}
                    company_pincode={companyPincode}
                    company_address={companyAddress}
                    set_company_name={setCompanyName}
                    set_company_phone={setCompanyPhone}
                    set_company_pincode={setCompanyPincode}
                    set_company_address={setCompanyAddress}
                    isResellerCompany={isResellerCompany}
                    isOwnCompany={isOwnCompany}
                    locations={locations}
                    selectedLocation={selectedLocation}
                    setSelectedLocation={setSelectedLocation}
                    countryCodeDirections={countryCodeDirections}
                    selectedCountryCodeDirections={selectedCountryCodeDirections}
                    setSelectedCountryCodeDirections={setSelectedCountryCodeDirections}
                  />

                  <div className="row g-3 mt-1 company-edit-form__row">
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>Company Expiry Date</Form.Label>
                      <CalendarDateInput
                        value={companyExpiryDate}
                        onChange={(e) => {
                          const value = e;
                          if (
                            !isDemoCompany &&
                            parentExpiryDate &&
                            value &&
                            value > parentExpiryDate
                          ) {
                            toast.error(
                              `Company expiry cannot be later than ${parentExpiryDate} (your own expiry date).`,
                            );
                            setCompanyExpiryDate(parentExpiryDate);
                          } else {
                            setCompanyExpiryDate(value);
                          }
                        }}
                        max={!isDemoCompany && parentExpiryDate ? parentExpiryDate : undefined}
                        disabled={isDemoCompany}
                      />
                      <small className="text-muted d-block mt-1">
                        {isDemoCompany
                          ? 'Managed automatically for demo companies (extended by the demo expiry days on each recharge). Cannot be edited manually.'
                          : parentExpiryDate
                          ? `Can be set up to ${formatExpiryForDisplay(parentExpiryDate)}.`
                          : ''}
                      </small>
                      {!isDemoCompany &&
                      originalCompanyExpiryDate &&
                      companyExpiryDate &&
                      companyExpiryDate < originalCompanyExpiryDate ? (
                        <small className="d-block mt-1 text-warning">
                          You are reducing this expiry. Every user and nested company below this
                          one whose expiry is later than{' '}
                          {formatExpiryForDisplay(companyExpiryDate)} will be lowered to that date
                          on save. Records that already expire earlier keep their own date.
                        </small>
                      ) : null}
                    </Form.Group>
                  </div>
                  </>
                )}

                {/* ── Step 1: Setup ───────────────────────────────── */}
                {stepper.current === 1 && (
                  <>
                  <div className="row g-3 mt-1 company-edit-form__row">
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label className="d-flex align-items-center w-100">
                        Call Strategy
                        <SelectAllToggle
                          allIds={callStrategies.map((o) => o.value)}
                          selectedIds={selectedCallStrategy}
                          onChange={setSelectedCallStrategy}
                        />
                      </Form.Label>
                      <Select
                        className="theme-react-select"
                        classNamePrefix="theme-react-select"
                        options={callStrategies}
                        value={callStrategies.filter((l) => selectedCallStrategy.includes(l.value))}
                        onChange={(selected) =>
                          setSelectedCallStrategy(selected.map((s) => s.value))
                        }
                        isMulti
                        placeholder="Select Call Strategy"
                      />
                    </Form.Group>
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label className="d-flex align-items-center w-100">
                        Dialer Group
                        <SelectAllToggle
                          allIds={groups.map((o) => o.value)}
                          selectedIds={selectedGroup}
                          onChange={setSelectedGroup}
                        />
                      </Form.Label>
                      <Select
                        className="theme-react-select"
                        classNamePrefix="theme-react-select"
                        options={groups}
                        value={groups.filter((g) => selectedGroup.includes(g.value))}
                        onChange={(selected) => setSelectedGroup(selected.map((s) => s.value))}
                        isMulti
                        placeholder="Select Dialer Group"
                      />
                    </Form.Group>
                  </div>

                  <div className="row g-3 mt-1">
                    <Form.Group className="col-12 col-lg-6 form-group mb-0">
                      <Form.Label>TTS Plan</Form.Label>
                      <Form.Select
                        value={tts_plan_id}
                        className="p-2"
                        onChange={(e) => setTtsPlanId(e.target.value)}
                      >
                        <option value="">Select TTS Plan</option>
                        {ttsPlanList.map((val, idx) => (
                          <option key={idx} value={val.id}>
                            {val.plan_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>

                  <div className="company-type-section-v2 company-edit-form__type-section">
                    <Form.Label className="ccf-field-label d-block">Company type</Form.Label>
                    <div className="row g-3">
                      {isDemoCompany && (
                        <div className="col-12 col-md-6">
                          <div className="company-type-card-v2 is-selected company-edit-form__type-card--readonly">
                            <span className="company-type-card-v2__radio" aria-hidden="true" />
                            <span className="company-type-card-v2__icon company-type-card-v2__icon--demo">
                              <FiActivity size={22} />
                            </span>
                            <div className="company-type-card-v2__body">
                              <div className="company-type-card-v2__title">Demo Company</div>
                              <div className="company-type-card-v2__desc">
                                This company is marked as a demo company and cannot be changed here
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {isResellerCompany && (
                        <div className="col-12 col-md-6">
                          <div className="company-type-card-v2 is-selected company-edit-form__type-card--readonly">
                            <span className="company-type-card-v2__radio" aria-hidden="true" />
                            <span className="company-type-card-v2__icon company-type-card-v2__icon--reseller">
                              <FaBuilding size={20} />
                            </span>
                            <div className="company-type-card-v2__body">
                              <div className="company-type-card-v2__title">Reseller Company</div>
                              <div className="company-type-card-v2__desc">
                                Can create and manage child companies
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {isOwnCompany && (
                        <div className="col-12 col-md-6">
                          <div className="company-type-card-v2 is-selected company-edit-form__type-card--readonly">
                            <span className="company-type-card-v2__radio" aria-hidden="true" />
                            <span className="company-type-card-v2__icon company-type-card-v2__icon--enduser">
                              <FiUsers size={22} />
                            </span>
                            <div className="company-type-card-v2__body">
                              <div className="company-type-card-v2__title">End User</div>
                              <div className="company-type-card-v2__desc">
                                Operates as a single end-user company
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {!isDemoCompany && (
                  <div className="company-type-section-v2 mt-3">
                    <Form.Label className="ccf-field-label d-block">Billing Plans</Form.Label>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="company-type-card-v2 is-selected company-edit-form__type-card--readonly">
                          <div className="company-type-card-v2__body">
                            <div className="company-type-card-v2__title">{billingPlanText}</div>
                            <div className="company-type-card-v2__desc">
                              {billingCreditText
                                ? `${billingCreditText} This plan cannot be changed here.`
                                : 'Billing plan is set when the company is created and cannot be changed here.'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                  </>
                )}

                {/* ── Step 2: Licence + Submit ────────────────────── */}
                {stepper.current === 2 && (
                  <>
                  <LicenceAllocationPanel
                    showNote={Boolean(licencePanelNote)}
                    noteMessage={licencePanelNote}
                    footerNote={
                      loginUserRole !== USER_ROLES.SUPERADMIN
                        ? notes.find((n) => Number(n.priority) === 2)?.message ?? ''
                        : ''
                    }
                    columns={[
                      {
                        key: 'role_ccadmin',
                        icon: FaHeadset,
                        iconModifier: 'callcenter',
                        title: 'Call Centers',
                        description: 'Number of call centers',
                        value: parseInt(String(callCenterAdminTeamlead), 10) || 0,
                        onChange: (val) =>
                          setCallCenterAdminTeamlead(validateInput(String(val), 'role_ccadmin')),
                        onDecrement: () =>
                          stepLicence('role_ccadmin', callCenterAdminTeamlead, setCallCenterAdminTeamlead, -1),
                        onIncrement: () =>
                          stepLicence('role_ccadmin', callCenterAdminTeamlead, setCallCenterAdminTeamlead, 1),
                        disabledMinus: (parseInt(String(callCenterAdminTeamlead), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'role_ccadmin',
                          parseInt(String(callCenterAdminTeamlead), 10) || 0,
                        ),
                        error: errors.role_ccadmin,
                        readOnly: isDemoCompany,
                      },
                      {
                        key: 'agent',
                        icon: FiUsers,
                        iconModifier: 'agent',
                        title: 'Total Agent',
                        description: 'Total agents allowed',
                        value: parseInt(String(agent), 10) || 0,
                        onChange: (val) => setAgent(validateInput(String(val), 'agent')),
                        onDecrement: () => stepLicence('agent', agent, setAgent, -1),
                        onIncrement: () => stepLicence('agent', agent, setAgent, 1),
                        disabledMinus: (parseInt(String(agent), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('agent', parseInt(String(agent), 10) || 0),
                        error: errors.agent,
                        readOnly: isDemoCompany,
                      },
                      {
                        key: 'team_lead',
                        icon: FiUserCheck,
                        iconModifier: 'agent',
                        title: 'Total Team Lead',
                        description: 'Total number of team leads',
                        value: parseInt(String(teamLead), 10) || 0,
                        onChange: (val) => setTeamLead(validateInput(String(val), 'team_lead')),
                        onDecrement: () => stepLicence('team_lead', teamLead, setTeamLead, -1),
                        onIncrement: () => stepLicence('team_lead', teamLead, setTeamLead, 1),
                        disabledMinus: (parseInt(String(teamLead), 10) || 0) <= 0,
                        disabledPlus:
                          !canIncrementLicence('team_lead', parseInt(String(teamLead), 10) || 0) ||
                          (parseInt(String(teamLead), 10) || 0) >= (parseInt(String(agent), 10) || 0),
                        error: errors.team_lead,
                        readOnly: isDemoCompany,
                      },
                      {
                        key: 'live_agent',
                        icon: FiActivity,
                        iconModifier: 'live',
                        title: 'Live Agent',
                        description: 'Concurrent live agents',
                        value: parseInt(String(liveAgent), 10) || 0,
                        onChange: (val) => setLiveAgent(validateInput(String(val), 'live_agent')),
                        onDecrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, -1),
                        onIncrement: () => stepLicence('live_agent', liveAgent, setLiveAgent, 1),
                        disabledMinus: (parseInt(String(liveAgent), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'live_agent',
                          parseInt(String(liveAgent), 10) || 0,
                        ),
                        error: errors.live_agent,
                        readOnly: isDemoCompany,
                      },
                      {
                        key: 'channel',
                        icon: FiShare2,
                        iconModifier: 'channel',
                        title: 'Channels',
                        description: 'Communication channels',
                        value: parseInt(String(channel), 10) || 0,
                        onChange: (val) => setChannel(validateInput(String(val), 'channel')),
                        onDecrement: () => stepLicence('channel', channel, setChannel, -1),
                        onIncrement: () => stepLicence('channel', channel, setChannel, 1),
                        disabledMinus: (parseInt(String(channel), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('channel', parseInt(String(channel), 10) || 0),
                        error: errors.channel,
                        readOnly: isDemoCompany,
                      },
                      {
                        key: 'ai_agent',
                        icon: FiCpu,
                        iconModifier: 'ai',
                        title: 'AI Agents',
                        description: 'AI agents this call centre can create',
                        value: parseInt(String(aiAgent), 10) || 0,
                        onChange: (val) => setAiAgent(validateInput(String(val), 'ai_agent')),
                        onDecrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, -1),
                        onIncrement: () => stepLicence('ai_agent', aiAgent, setAiAgent, 1),
                        disabledMinus: (parseInt(String(aiAgent), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'ai_agent',
                          parseInt(String(aiAgent), 10) || 0,
                        ),
                        error: errors.ai_agent,
                      },
                      {
                        key: 'ai_agent_live_channel',
                        icon: FiShare2,
                        iconModifier: 'ai',
                        title: 'AI Agent Live Channels',
                        description: 'Concurrent AI agent call channels',
                        value: parseInt(String(aiAgentLiveChannel), 10) || 0,
                        onChange: (val) =>
                          setAiAgentLiveChannel(validateInput(String(val), 'ai_agent_live_channel')),
                        onDecrement: () =>
                          stepLicence(
                            'ai_agent_live_channel',
                            aiAgentLiveChannel,
                            setAiAgentLiveChannel,
                            -1,
                          ),
                        onIncrement: () =>
                          stepLicence(
                            'ai_agent_live_channel',
                            aiAgentLiveChannel,
                            setAiAgentLiveChannel,
                            1,
                          ),
                        disabledMinus: (parseInt(String(aiAgentLiveChannel), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'ai_agent_live_channel',
                          parseInt(String(aiAgentLiveChannel), 10) || 0,
                        ),
                        error: errors.ai_agent_live_channel,
                      },
                      {
                        key: 'cps',
                        icon: FiPhone,
                        iconModifier: 'channel',
                        title: 'CPS',
                        description: 'Calls per second',
                        value: parseInt(String(cps), 10) || 0,
                        onChange: (val) => setCps(validateInput(String(val), 'cps')),
                        onDecrement: () => stepLicence('cps', cps, setCps, -1),
                        onIncrement: () => stepLicence('cps', cps, setCps, 1),
                        disabledMinus: (parseInt(String(cps), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('cps', parseInt(String(cps), 10) || 0),
                        error: errors.cps,
                      },
                      {
                        key: 'mobile',
                        icon: FiSmartphone,
                        iconModifier: 'mobile',
                        title: 'Mobile License',
                        description: 'Mobile Device licences',
                        value: parseInt(String(mobile), 10) || 0,
                        onChange: (val) => setMobile(validateInput(String(val), 'mobile')),
                        onDecrement: () => stepLicence('mobile', mobile, setMobile, -1),
                        onIncrement: () => stepLicence('mobile', mobile, setMobile, 1),
                        disabledMinus: (parseInt(String(mobile), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence('mobile', parseInt(String(mobile), 10) || 0),
                        error: errors.mobile,
                      },
                      {
                        key: 'demo_live_agents',
                        icon: FiActivity,
                        iconModifier: 'live',
                        title: 'Demo Live Agents (Reseller)',
                        description: 'Demo live-agent pool for this reseller tree',
                        value: parseInt(String(demoLiveAgents), 10) || 0,
                        hidden: !(loginUserRole === USER_ROLES.SUPERADMIN
                          ? Boolean(isResellerCompany)
                          : Boolean(isResellerCompany) || isDemoCompany),
                        onChange: (val) =>
                          setDemoLiveAgents(validateInput(String(val), 'demo_live_agents')),
                        onDecrement: () =>
                          stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, -1),
                        onIncrement: () =>
                          stepLicence('demo_live_agents', demoLiveAgents, setDemoLiveAgents, 1),
                        disabledMinus: (parseInt(String(demoLiveAgents), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'demo_live_agents',
                          parseInt(String(demoLiveAgents), 10) || 0,
                        ),
                        error: errors.demo_live_agents,
                      },
                      {
                        key: 'company',
                        icon: FaBuilding,
                        iconModifier: 'company',
                        title: 'Company',
                        description: 'Additional companies',
                        value: parseInt(String(asCompany), 10) || 0,
                        hidden: isOwnCompany,
                        onChange: (val) => setAsCompany(validateInput(String(val), 'company')),
                        onDecrement: () => stepLicence('company', asCompany, setAsCompany, -1),
                        onIncrement: () => stepLicence('company', asCompany, setAsCompany, 1),
                        disabledMinus: (parseInt(String(asCompany), 10) || 0) <= 0,
                        disabledPlus: !canIncrementLicence(
                          'company',
                          parseInt(String(asCompany), 10) || 0,
                        ),
                        error: errors.company,
                      },
                    ]}
                    availableSummary={
                      loginUserRole !== USER_ROLES.SUPERADMIN ? (
                        <div className="licence-available-summary">
                          <h6 className="licence-available-summary__title">
                            Available Licence (Allocated Company)
                          </h6>
                          <div className="licence-available-summary__row">
                            <span>
                              Call Centers: {parentLicence?.available_licence?.role_ccadmin ?? 0}
                            </span>
                            <span>Agent: {parentLicence?.available_licence?.role_ccagent ?? 0}</span>
                            <span>
                              Total Team Lead: {parentLicence?.available_licence?.role_teamlead ?? 0}
                            </span>
                            <span>Live Agent: {parentLicence?.available_licence?.live_agent ?? 0}</span>
                            <span>Channels: {parentLicence?.available_licence?.channel ?? 0}</span>
                            <span>AI Agents: {parentLicence?.available_licence?.ai_agent ?? 0}</span>
                            <span>
                              AI Live Channels:{' '}
                              {parentLicence?.available_licence?.ai_agent_live_channel ?? 0}
                            </span>
                            <span>CPS: {parentLicence?.available_licence?.cps ?? 0}</span>
                            <span>Mobile: {parentLicence?.available_licence?.mobile ?? 0}</span>
                            {!isOwnCompany && (
                              <span>Company: {parentLicence?.available_licence?.company ?? 0}</span>
                            )}
                          </div>
                        </div>
                      ) : loginUserRole === USER_ROLES.SUPERADMIN ? (
                        <div className="licence-available-summary">
                          <h6 className="licence-available-summary__title">
                            Available Licence (This Company)
                          </h6>
                          <div className="licence-available-summary__row">
                            <span>Call Centers: {availableLicence?.role_ccadmin ?? 0}</span>
                            <span>Agent: {availableLicence?.role_ccagent ?? 0}</span>
                            <span>Total Team Lead: {availableLicence?.role_teamlead ?? 0}</span>
                            <span>Live Agent: {availableLicence?.live_agent ?? 0}</span>
                            <span>Channels: {availableLicence?.channel ?? 0}</span>
                            <span>AI Agents: {availableLicence?.ai_agent ?? 0}</span>
                            <span>
                              AI Live Channels: {availableLicence?.ai_agent_live_channel ?? 0}
                            </span>
                            <span>CPS: {availableLicence?.cps ?? 0}</span>
                            <span>Mobile: {availableLicence?.mobile ?? 0}</span>
                            {!isOwnCompany && <span>Company: {availableLicence?.company ?? 0}</span>}
                          </div>
                        </div>
                      ) : null
                    }
                  />

                  <div className="licence-used-summary">
                    <h6 className="licence-used-summary__title">
                      Used Licence (Current Company)
                    </h6>
                    <div className="licence-used-summary__row">
                      <span>Call Centers: {usedLicence?.role_ccadmin ?? 0}</span>
                      <span>Agent: {usedLicence?.role_ccagent ?? 0}</span>
                      <span>Total Team Lead: {usedLicence?.role_teamlead ?? 0}</span>
                      <span>Live Agent: {usedLicence?.live_agent ?? 0}</span>
                      <span>Channels: {usedLicence?.channel ?? 0}</span>
                      <span>AI Agents: {usedLicence?.ai_agent ?? 0}</span>
                      <span>AI Live Channels: {usedLicence?.ai_agent_live_channel ?? 0}</span>
                      <span>CPS: {usedLicence?.cps ?? 0}</span>
                      <span>Mobile: {usedLicence?.mobile ?? 0}</span>
                      {!isOwnCompany && <span>Company: {usedLicence?.company ?? 0}</span>}
                    </div>
                  </div>

                  </>
                )}

                {/* ── Step 3: Branding + Submit ──────────────────── */}
                {stepper.current === 3 && (
                  <div className="company-type-section-v2 mt-3">
                    <Form.Label className="ccf-field-label d-block">Panel Branding</Form.Label>
                    <p className="text-muted small mb-3">
                      Shown in the sidebar to every user assigned to this company, including its
                      call centre admins and agents. Leave blank to inherit the parent company's
                      branding.
                    </p>
                    <CompanyBrandingFields
                      domain={domain}
                      setDomain={setDomain}
                      domainError={domainError}
                      setDomainError={setDomainError}
                      panelDisplayName={panelDisplayName}
                      setPanelDisplayName={setPanelDisplayName}
                      logoPreview={logoPreview}
                      logoError={logoError}
                      onLogoChange={handleLogoChange}
                      existingLogoUrl={resolvedStoredLogoUrl}
                    />

                  <div className="form-actions company-edit-form__actions">
                    <Button
                      onClick={submitForm}
                      type="button"
                      variant="btn btn-primary"
                      disabled={
                        buttonDisable || Object.values(errors).some((error) => error !== '')
                      }
                    >
                      {buttonDisable ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                          &nbsp; Updating...
                        </>
                      ) : (
                        'Update Company'
                      )}
                    </Button>
                  </div>
                  </div>
                )}

                {/* Wizard nav — Back on every step > 0; Next until the
                    last step (which owns the Update Company button). */}
                <div className="mt-6 flex items-center justify-between">
                  <ShadPageButton
                    type="button"
                    variant="ghost"
                    onClick={stepper.prev}
                    disabled={stepper.current === 0}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </ShadPageButton>
                  {stepper.current < EDIT_COMPANY_STEPS.length - 1 && (
                    <ShadPageButton
                      type="button"
                      onClick={() => {
                        if (stepper.current === 3 && domainError) return;
                        stepper.next();
                      }}
                    >
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </ShadPageButton>
                  )}
                </div>
                </form>
              </div>
            </div>
          </ShadcnCard>
        </Col>
      </Row>
      </div>
    </FillPage>
  );
};

export default CompanyUpdate;
